import React from "react"
import styles from "./message.module.css"
import { getLocalDate, getNameUserById } from "../../utils/utils"
import { MdAccessTime, MdCall, MdCallEnd, MdCallMade, MdCallMissed, MdCallReceived, MdCheck, MdDoneAll, MdErrorOutline, MdFileDownload, MdOutlinePhoneInTalk } from "react-icons/md"
import { useContact } from "../../stores/useContactStore"
import { useWebSocketChatContext } from "../../contexts/webSocketChatContext"
import AudioPlayer from "../Form/AudioPlayer"
import { formatSecondsToTime } from "../../utils/formatter"

const SystemMessage = ({ message, allUsersGroup, platform }) => {
    const contact = useContact(message?.contact_id) || {}
    const { getContactByContactDataAndPlatform } = useWebSocketChatContext()
    const contactData = getContactByContactDataAndPlatform(contact, platform)

    const formatMessageSystem = (message) => {
        const prefix = message?.user_id ? `${getNameUserById(allUsersGroup, message.user_id)}` : ""
        return `${prefix} ${message?.content?.text || "-"} em ${getLocalDate(message.created_at).format("DD/MM/YYYY HH:mm:ss")}`
    }

    const getDeliveryStatus = () => {
        if (message?.failed) return { label: "Falha no envio", icon: <MdErrorOutline />, tone: styles.callSystemDanger }
        if (message?.read) return { label: "Lida", icon: <MdDoneAll />, tone: styles.callSystemRead, at: message?.metadata?.read_at }
        if (message?.delivered) return { label: "Entregue", icon: <MdDoneAll />, tone: styles.callSystemRead, at: message?.metadata?.delivered_at }
        if (message?.sent) return { label: "Enviada", icon: <MdCheck />, tone: "", at: message?.metadata?.sent_at }
        return { label: "Processando", icon: <MdAccessTime />, tone: "" }
    }

    const renderCallPermissionRequest = () => {
        const status = getDeliveryStatus()
        const userName = message?.user_id ? getNameUserById(allUsersGroup, message.user_id) : "Atendente"
        const templateName = message?.content?.template_name
        const messageText = message?.content?.message_text

        return (
            <div className={`${styles.callSystem} ${status.tone}`} data-message-id={message?.message_id}>
                <div className={styles.callSystemIcon}><MdOutlinePhoneInTalk /></div>
                <div className={styles.callSystemContent}>
                    <strong>{userName} enviou uma solicitação de autorização para ligação</strong>
                    {messageText && <div className={styles.callSystemPreview}>{messageText}</div>}
                    <div className={styles.callSystemMeta}>
                        <span>
                            {templateName
                                ? `Template: ${templateName}${message?.content?.template_language ? ` · ${message.content.template_language}` : ""}`
                                : "Solicitação rápida"}
                        </span>
                        <span>{getLocalDate(message.created_at).format("DD/MM/YYYY HH:mm")}</span>
                        <span
                            className={styles.callSystemDelivery}
                            title={message?.error || `${status.label}${status.at ? ` em ${getLocalDate(status.at).format("DD/MM/YYYY HH:mm:ss")}` : ""}`}
                        >
                            {status.icon} {status.label}
                        </span>
                    </div>
                </div>
            </div>
        )
    }

    const renderCallPermissionResponse = () => {
        const accepted = message?.content?.accepted !== false && message?.content?.permission_status !== "no_permission"
        const isPermanent = message?.content?.permission_status === "permanent"
        const expirationTimestamp = Number(message?.content?.expiration_timestamp)
        const expirationDate = Number.isFinite(expirationTimestamp) && expirationTimestamp > 0
            ? getLocalDate(new Date(expirationTimestamp * 1000)).format("DD/MM/YYYY HH:mm")
            : ""
        const contactName = contactData?.name || contact?.name || "O contato"
        const detail = accepted
            ? isPermanent
                ? "Autorização permanente"
                : `Autorização temporária${expirationDate ? ` até ${expirationDate}` : ""}`
            : "A solicitação foi recusada"

        return (
            <div className={`${styles.callSystem} ${accepted ? styles.callSystemSuccess : styles.callSystemDanger}`} data-message-id={message?.message_id}>
                <div className={styles.callSystemIcon}>{accepted ? <MdCheck /> : <MdOutlinePhoneInTalk />}</div>
                <div className={styles.callSystemContent}>
                    <strong>{contactName} {accepted ? "permitiu chamadas pelo WhatsApp" : "não permitiu chamadas pelo WhatsApp"}</strong>
                    <div className={styles.callSystemMeta}>
                        <span>{detail}</span>
                        <span>{getLocalDate(message.created_at).format("DD/MM/YYYY HH:mm")}</span>
                    </div>
                </div>
            </div>
        )
    }

    const renderCallSession = () => {
        const content = message?.content || {}
        const legacyStatus = {
            call_started: "calling",
            call_received: "incoming",
            call_terminated: Number(content?.duration) > 0 ? "completed" : "ended",
            call_rejected: "rejected",
        }[content?.event]
        const callStatus = content?.call_status || legacyStatus || "calling"
        const incoming = content?.direction === "USER_INITIATED" || message?.direction === "received"
        const contactName = contactData?.name || contact?.name || "o contato"
        const userName = message?.user_id ? getNameUserById(allUsersGroup, message.user_id) : ""
        const duration = Math.max(Number(content?.duration) || 0, 0)
        const statusConfig = {
            calling: { label: "Chamando", icon: <MdCallMade />, tone: styles.callSessionActive },
            ringing: { label: "Chamando", icon: <MdCallMade />, tone: styles.callSessionActive },
            incoming: { label: "Recebida", icon: <MdCallReceived />, tone: styles.callSessionActive },
            answered: { label: "Em andamento", icon: <MdCall />, tone: styles.callSessionSuccess },
            completed: { label: "Atendida", icon: <MdCallEnd />, tone: styles.callSessionSuccess },
            missed: { label: "Perdida", icon: <MdCallMissed />, tone: styles.callSessionDanger },
            not_answered: { label: "Não atendida", icon: <MdCallMissed />, tone: styles.callSessionNeutral },
            rejected: { label: "Recusada", icon: <MdCallEnd />, tone: styles.callSessionDanger },
            failed: { label: "Falhou", icon: <MdErrorOutline />, tone: styles.callSessionDanger },
            ended: { label: "Encerrada", icon: <MdCallEnd />, tone: styles.callSessionNeutral },
        }[callStatus] || { label: "Chamada", icon: <MdCall />, tone: styles.callSessionNeutral }
        const startedAt = content?.started_at || content?.start_time || message?.created_at
        const endedAt = content?.ended_at || content?.end_time
        const firstError = Array.isArray(content?.errors) ? content.errors[0] : null
        const errorMessage = firstError?.error_data?.details || firstError?.message || firstError?.title

        return (
            <div className={`${styles.callSystem} ${styles.callSession} ${statusConfig.tone}`} data-message-id={message?.message_id}>
                <div className={styles.callSystemIcon}>{statusConfig.icon}</div>
                <div className={styles.callSystemContent}>
                    <div className={styles.callSessionHeader}>
                        <strong>{incoming ? `Chamada recebida de ${contactName}` : `Chamada pelo WhatsApp com ${contactName}`}</strong>
                        <span className={styles.callSessionStatus}>{statusConfig.label}</span>
                    </div>
                    <div className={styles.callSessionDetails}>
                        {userName && !incoming && <span>Iniciada por {userName}</span>}
                        <span>Início: {getLocalDate(startedAt).format("DD/MM/YYYY HH:mm")}</span>
                        {endedAt && <span>Fim: {getLocalDate(endedAt).format("HH:mm")}</span>}
                        {duration > 0 && <span>Duração: {formatSecondsToTime(Math.ceil(duration), false)}</span>}
                    </div>
                    {errorMessage && <div className={styles.callSessionError}>{errorMessage}</div>}
                    {content?.recording && content?.url &&
                        <div className={styles.callRecording}>
                            <div className={styles.callRecordingHeader}>
                                <span>
                                    <strong>Gravação da chamada</strong>
                                    {content?.seconds > 0 && <small>{formatSecondsToTime(Math.ceil(content.seconds), false)}</small>}
                                </span>
                                <a href={content.url} target="_blank" rel="noreferrer" download={content?.name || "gravacao-chamada.webm"} title="Baixar gravação">
                                    <MdFileDownload />
                                </a>
                            </div>
                            <AudioPlayer src={content.url} preload="metadata" pink={false} />
                        </div>
                    }
                </div>
            </div>
        )
    }

    if (message?.content?.system_type === "whatsapp_call") {
        if (message?.content?.event === "permission_requested") return renderCallPermissionRequest()
        if (message?.content?.event === "permission_updated") return renderCallPermissionResponse()
        if (["call_session", "call_started", "call_received", "call_terminated", "call_rejected"].includes(message?.content?.event)) return renderCallSession()
    }

    return (
        <div className={styles.system}>
            <span>{formatMessageSystem(message)}</span>
        </div>
    )
}

export default SystemMessage
